import React from 'react';
import type { GroupInvitation } from '@ext-bookmark/shared/types';

interface InvitationCardProps {
  invitation: GroupInvitation;
  onAccept?: (invitationId: string) => void;
  onReject?: (invitationId: string) => void;
  showActions?: boolean; // 是否显示操作按钮（在分组管理页面可能不显示）
}

const InvitationCard: React.FC<InvitationCardProps> = ({
  invitation,
  onAccept,
  onReject,
  showActions = true,
}) => {
  const isPending = invitation.status === 'pending';
  const isExpired = invitation.status === 'expired' || new Date(invitation.expiresAt) < new Date();
  const isAccepted = invitation.status === 'accepted';
  const isRejected = invitation.status === 'rejected';

  const getStatusBadge = () => {
    if (isPending && !isExpired) {
      return (
        <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded">
          待处理
        </span>
      );
    }
    if (isAccepted) {
      return (
        <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
          已接受
        </span>
      );
    }
    if (isRejected) {
      return (
        <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded">
          已拒绝
        </span>
      );
    }
    if (isExpired) {
      return (
        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
          已过期
        </span>
      );
    }
    return null;
  };

  const handleAccept = () => {
    if (onAccept && isPending && !isExpired) {
      onAccept(invitation.id);
    }
  };

  const handleReject = () => {
    if (onReject && isPending && !isExpired) {
      onReject(invitation.id);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {invitation.inviteeName || invitation.inviteeEmail || '未知用户'}
          </div>
          {getStatusBadge()}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          邀请于 {new Date(invitation.createdAt).toLocaleDateString('zh-CN')}
          {isPending && !isExpired && (
            <span className="ml-2">
              (过期时间: {new Date(invitation.expiresAt).toLocaleDateString('zh-CN')})
            </span>
          )}
        </div>
      </div>
      {showActions && isPending && !isExpired && (
        <div className="flex items-center gap-2">
          <button
            onClick={handleAccept}
            className="px-3 py-1 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            接受
          </button>
          <button
            onClick={handleReject}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            拒绝
          </button>
        </div>
      )}
    </div>
  );
};

export default InvitationCard;
